<template lang="pug">
//- pre.foo {{ settings }}
.input-field(
    v-if="edit"
    ref="container"
    :class="{ 'has-block-settings': blockSettings && blockSettings.length, 'divider': ['hr', 'separator', 'divider'].includes(config.type), 'inline-comment': comment.value && comment.value.length && comment.type === 'inline', 'is-banner-editor': config.type === 'banner-editor' }"
)
    template(v-if="['hr', 'separator', 'divider'].includes(config.type)")
        template(v-if="config.name")
            //- hr
            label {{ $t(config.name) }}
        hr
    .flex-fields(v-else)
        .input(:class="{ 'visible-block-settings': visibleBlockSettings }")
            el-form-item(:label="$t(config.name)" :prop="validationPath" :size="size")
                template(v-if="defaultLanguage && ['textfield', 'rte', 'textarea'].includes(config.type)")
                    .translation-origin(v-if="translationOriginValue" :class="{ visible: showTranslationOrigin }")
                        button.language(type="button" @click="showTranslationOrigin = !showTranslationOrigin")
                            span(v-if="showTranslationOrigin") {{ getLanguageName(defaultLanguage.code) }}
                            span(v-else) {{ $t('Show original text') }}
                            span.circle.fi.fis(:class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`")
                        transition(name="show-translation")
                            .translation-origin-content(
                                v-if="showTranslationOrigin"
                                :class="{'ql-editor': config.type === 'rte'}"
                                v-html="translationOriginValue"
                            )
                    //- .translation-target(v-if="content.language && content.language.code !== defaultLanguage.code")
                    //-     span.flag.circle.fi.fis(:class="`fi-${content.language.flag || getLanguageFlag(content.language.code)}`")
                el-input(
                    v-if="config.type === 'textfield'"
                    type="text"
                    v-model="value"
                    :placeholder="config.placeholder"
                    :maxlength="maxlength"
                    :show-word-limit="true"
                )
                el-input(
                    v-else-if="config.type === 'charfield'"
                    type="text"
                    v-model="value"
                    :placeholder="config.placeholder"
                )
                el-input-number(
                    v-else-if="config.type === 'numberfield'"
                    v-model="numberValue"
                    :min="config.min != null ? config.min : Infinity"
                    :max="config.max != null ? config.max : Infinity"
                    :step="config.step"
                    :precision="config.precision"
                    :input-size="size"
                    controls-position="right"
                    :placeholder="config.placeholder"
                )
                el-slider(
                    v-else-if="config.type === 'slider'"
                    v-model="numberValue"
                    :min="config.min || 0"
                    :max="config.max || 100"
                    :step="config.step || 1"
                    :input-size="size"
                    :show-tooltip="false"
                    show-input
                )
                //- datepicker: Stores value as ISO 8601 with timezone offset (e.g., 2024-01-15T00:00:00+01:00)
                el-date-picker(
                    v-else-if="config.type === 'datepicker'"
                    v-model="dateValue"
                    type="date"
                    :placeholder="config.placeholder"
                    :format="$t('yyyy-MM-dd')"
                    :size="size"
                )
                //- datetimepicker: Stores value as ISO 8601 with timezone offset (e.g., 2024-01-15T14:30:00+01:00)
                el-date-picker(
                    v-else-if="config.type === 'datetimepicker'"
                    v-model="dateValue"
                    type="datetime"
                    :placeholder="config.placeholder"
                    :format="$t('yyyy-MM-dd') + ' HH:mm'"
                    :size="size"
                )
                template(v-else-if="config.type === 'rte'")
                    quill-editor(
                        v-if="quillConfig.bounds"
                        ref="quill"
                        v-model="value"
                        :data-placeholder="config.placeholder || ''"
                        :options="quillConfig"
                    )
                    .dummy.ql-editor(
                        v-else
                        v-html="value"
                        disabled
                        rows="2"
                    )
                el-input(
                    v-else-if="config.type === 'textarea'"
                    type="textarea"
                    :autosize="{ minRows: 4, maxRows: 40 }"
                    :placeholder="config.placeholder"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="config.type === 'code-editor:html'"
                    language="html"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="config.type === 'code-editor:twig'"
                    language="twig"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="['code-editor:js', 'code-editor:javascript'].includes(config.type)"
                    language="js"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="config.type === 'code-editor:json'"
                    language="json"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="config.type === 'code-editor:css'"
                    language="css"
                    v-model="value"
                )
                table-editor(
                    v-else-if="config.type === 'table-editor'"
                    v-model="value"
                    :edit="edit"
                )
                banner-editor(
                    v-else-if="config.type === 'banner-editor'"
                    v-model="value"
                    :edit="edit"
                    :ci-colors="templateCiColors"
                    :body-font="templateBodyFont"
                    :options="options"
                    :open-at-slide="bannerEditorOpenAt"
                )
                .link(v-else-if="config.type === 'linkfield'")
                    el-radio-group(v-if="showLinkTypes" v-model="linkType" size="mini")
                        el-radio-button(
                            v-if="isLinkTypeActive('internal')"
                            :name="`link-type-${content.id}`"
                            label="internal"
                        ) {{ $t('Internal') }}
                        el-radio-button(
                            v-if="isLinkTypeActive('post') && quillPostLinksFeatureEnabled && hasCategories"
                            :name="`link-type-${content.id}`"
                            label="post"
                        ) {{ $t('Post') }}
                        el-radio-button(
                            v-if="isLinkTypeActive('external')"
                            :name="`link-type-${content.id}`"
                            label="external"
                        ) {{ $t('External') }}
                        el-radio-button(
                            v-if="isLinkTypeActive('mailto')"
                            :name="`link-type-${content.id}`"
                            label="mailto"
                        ) {{ $t('Email') }}
                        el-radio-button(
                            v-if="isLinkTypeActive('tel')"
                            :name="`link-type-${content.id}`"
                            label="tel"
                        ) {{ $t('Phone') }}
                        el-radio-button(
                            v-if="isLinkTypeActive('media')"
                            :name="`link-type-${content.id}`"
                            label="media"
                        ) {{ $t('Media library') }}
                    transition(name="slide-vertical")
                        .internal(v-if="linkType === 'internal'")
                            .flex-row
                                Treeselect(
                                    v-model="linkMenuItem"
                                    :placeholder="linkHrefPlaceholder"
                                    :multiple="false"
                                    :options="treeselectPageOptions"
                                    :default-expand-level="Infinity"
                                    :close-on-select="true"
                                    :open-on-focus="true"
                                    :searchable="true"
                                    :normalizer="node => ({ label: node.title, ...node })"
                                    :clearable="true"
                                    :always-open="false"
                                )
                                    label(
                                        slot="option-label"
                                        slot-scope="{ node }"
                                        :class="{ optgroup: node.raw.optgroup }"
                                    )
                                        span {{ node.raw.title }}
                                        template(v-if="i18n && language && node.raw.subTitle")
                                            small {{ node.raw.subTitle }}
                                    div(
                                        slot="value-label"
                                        slot-scope="{ node }"
                                        :class="{ optgroup: node.raw.optgroup }"
                                    )
                                        span {{ node.raw.title }}
                                        template(v-if="i18n && language && node.raw.subTitle")
                                            small {{ node.raw.subTitle }}
                                el-input(
                                    v-if="linkAnchorSetting"
                                    type="text"
                                    v-model="linkAnchor"
                                    prefix-icon="fas el-icon-fa-hashtag"
                                    :placeholder="$t('Anchor')"
                                )
                                el-input(
                                    v-if="linkTextSetting"
                                    type="text"
                                    v-model="linkText"
                                    :placeholder="$t('Text')"
                                )
                        .post(v-else-if="linkType === 'post'")
                            .flex-row
                                el-select.is-full-width(
                                    v-model="modalHyperlinkPage"
                                    :placeholder="$t('Find a post ...')"
                                    filterable
                                    remote
                                    clearable
                                    :remote-method="findPost"
                                    :disabled="post.loading"
                                    :loading="post.loading"
                                )
                                    el-option(
                                        v-for="option in post.items"
                                        :key="option.id"
                                        :label="option.title"
                                        :value="option.id"
                                    )
                                        PostOptionPreview(:post="option")
                                el-input(
                                    v-if="linkAnchorSetting"
                                    type="text"
                                    v-model="linkAnchor"
                                    prefix-icon="fas el-icon-fa-hashtag"
                                    :placeholder="$t('Anchor')"
                                )
                        .external(v-else-if="['external', 'mailto', 'tel'].includes(linkType)")
                            .flex-row
                                el-input(type="text" v-model="linkHref" :placeholder="linkHrefPlaceholder")
                                el-input(v-if="linkTextSetting" type="text" v-model="linkText" :placeholder="$t('Text')")
                            el-checkbox(v-if="linkType === 'external'" v-model="linkTarget") {{ $t('Open link in new window/tab') }}
                        .media(v-else-if="linkType === 'media'")
                            .flex-row
                                el-input.is-full-width(type="text" v-model="linkHref" :placeholder="$t('No file selected')" readonly)
                                el-button(plain icon="el-icon-fa-folder-open fas" @click="mediaLibraryDialogVisible = true") {{ $t('Open media library') }}
                            el-input(v-if="linkTextSetting" type="text" v-model="linkText" :placeholder="$t('Text')")
                    image-library-dialog(
                        v-if="linkType === 'media'"
                        :visible.sync="mediaLibraryDialogVisible"
                        selection-type="file"
                        @select="selectFileFromLibrary"
                    )

                .select(v-else-if="config.type === 'select'")
                    el-select.is-full-width(
                        placeholder=""
                        v-model="value"
                        :disabled="remote.loading"
                        :loading="remote.loading"
                        :clearable="clearable"
                    )
                        el-option(
                            v-for="option in selectOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option.key"
                            :class="option.key"
                        )
                .select.multi(v-else-if="config.type === 'multi-select'")
                    el-select.is-full-width(
                        v-model="tags"
                        :disabled="remote.loading"
                        :loading="remote.loading"
                        multiple
                        filterable
                        clearable
                    )
                        el-option(
                            v-for="option in selectOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option"
                        )
                .select.tags(v-else-if="config.type === 'tags'")
                    el-select.is-full-width(
                        v-model="tags"
                        :loading="remote.loading"
                        multiple
                        filterable
                        clearable
                        allow-create
                        default-first-option
                    )
                        el-option(
                            v-for="option in selectOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option.key"
                        )

                .colorpicker(v-else-if="config.type === 'colorpicker'")
                    el-color-picker(
                        v-model="value"
                        color-format="hex"
                        :size="size"
                        :predefine="optionValues"
                    )
                .checkbox(v-else-if="config.type === 'switch'")
                    el-switch(v-model="value")

                .select.properties(v-else-if="['select:searchable-properties', 'multi-select:searchable-properties'].includes(config.type)")
                    el-select.is-full-width(
                        v-model="properties"
                        :multiple="['multi-select:searchable-properties'].includes(config.type)"
                        :disabled="!checkRestrictions('ex:shop')"
                        value-key="key"
                        filterable
                        clearable
                    )
                        el-option(
                            v-for="option in propertyOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option"
                        )
                .select.properties(v-else-if="['select:sortable-properties', 'multi-select:sortable-properties'].includes(config.type)")
                    el-select.is-full-width(
                        v-model="properties"
                        :multiple="['multi-select:sortable-properties'].includes(config.type)"
                        :disabled="!checkRestrictions('ex:shop')"
                        value-key="key"
                        filterable
                        clearable
                    )
                        el-option(
                            v-for="option in propertyOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option"
                        )
                .select.property-values(v-else-if="config.type === 'multi-select:property-values'")
                    el-select.is-full-width(
                        v-model="propertyValues"
                        :disabled="!checkRestrictions('ex:shop') || !valuesProperty"
                        :remote-method="findPropertyValues"
                        :loading="propertyValueSearch.loading"
                        value-key="key"
                        multiple
                        filterable
                        remote
                        clearable
                        @visible-change="open => open && findPropertyValues('')"
                    )
                        el-option(
                            v-for="option in propertyValueOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option"
                        )
                .select.properties(v-else-if="config.type === 'select:properties'")
                    el-select.is-full-width(
                        v-model="property"
                        :disabled="!checkRestrictions('ex:shop')"
                        value-key="key"
                        filterable
                        clearable
                    )
                        el-option(
                            v-for="option in propertyOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option"
                        )

                .select.member-tags(v-else-if="['select:member-tags', 'multi-select:member-tags'].includes(config.type)")
                    el-select.is-full-width(
                        v-model="memberTags"
                        :key="`member-tags:${memberTags.length}`"
                        :multiple="['multi-select:member-tags'].includes(config.type)"
                        :disabled="!checkRestrictions('ex:rstrct||ex:shop')"
                        value-key="key"
                        filterable
                        clearable
                        :allow-create="['multi-select:member-tags'].includes(config.type)"
                        :default-first-option="['multi-select:member-tags'].includes(config.type)"
                    )
                        el-option(
                            v-for="option in memberTagOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option"
                        )

                .select.customer-groups(v-else-if="['select:customer-groups', 'multi-select:customer-groups'].includes(config.type)")
                    el-select.is-full-width(
                        v-model="customerGroup"
                        :multiple="['multi-select:customer-groups'].includes(config.type)"
                        :disabled="!checkRestrictions('ex:shop')"
                        value-key="key"
                        filterable
                        clearable
                    )
                        el-option(
                            v-for="option in customerGroupOptions"
                            :key="option.key"
                            :label="option.name"
                            :value="option"
                        )

                .select.subscription-topics(v-else-if="['select:subscription-topics', 'multi-select:subscription-topics'].includes(config.type)")
                    el-select.is-full-width(
                        v-model="subscriptionTopics"
                        :key="`subscription-topics:${subscriptionTopics.length}`"
                        :multiple="['multi-select:subscription-topics'].includes(config.type)"
                        :disabled="!checkRestrictions('ex:newsletter')"
                        value-key="key"
                        filterable
                        clearable
                    )
                        el-option(
                            v-for="option in subscriptionTopicOptions"
                            :key="option.key"
                            :label="option.value"
                            :value="option"
                        )

                //- only for settings

                .image(v-else-if="config.key && config.type === 'images'")
                    image-field(:parent="section" :config="config")

                button.block-settings-toggle(
                    v-if="blockSettings && blockSettings.length"
                    type="button"
                    :class="config.type"
                    @click="visibleBlockSettings = ! visibleBlockSettings"
                )
                    span.el-icon-fa-cogs.fas

            transition(name="slide-vertical")
                .block-settings(v-if="blockSettingsSupported && blockSettings && blockSettings.length && visibleBlockSettings")
                    .settings-wrap
                        .setting(v-for="setting in blockSettings" :key="setting.key")
                            input-field(
                                :section="section"
                                :config="setting"
                                :content="contentSettingValues"
                                :options="options"
                                :section-settings="sectionSettings"
                                :section-setting-values="sectionSettingValues"
                                :edit="edit"
                                size="mini"
                                @change="updateSetting"
                            )

        .inline-settings(v-if="inlineSettingsSupported && inlineSettings && inlineSettings.length")
            .setting(v-for="setting in inlineSettings" :key="setting.key")
                input-field(
                    :section="section"
                    :config="setting"
                    :content="contentSettingValues"
                    :options="options"
                    :section-settings="sectionSettings"
                    :section-setting-values="sectionSettingValues"
                    :edit="edit"
                    @change="updateSetting"
                )

        image-library-dialog(
            v-if="config.type === 'rte'"
            :visible.sync="quillMediaLibraryDialogVisible"
            selection-type="file"
            @select="selectFileFromQuillLibrary"
        )
        .comment(v-if="comment.value && comment.value.length")
            .tooltip(v-if="comment.type === 'tooltip'")
                el-popover(
                    placement="right"
                    width="250"
                    trigger="hover"
                )
                    template(#default)
                        span(v-html="comment.value")
                    el-button(slot="reference" round) ?
            .inline(v-else) {{ comment.value }}

.preview-field(v-else)
    p(v-if="config.type === 'textfield' && config.key === 'title' && value")
        b {{ value }}
    p(v-else-if="config.type === 'textfield' && config.key === 'sub_title' && value")
        b {{ value }}
    div(v-else-if="config.type === 'rte' && value")
        //- workaround: flex-fields kept the overflow hidden stuff
        .rte(v-line-clamp:24="12" v-html="striptags(value)")
    .table-editor-preview(v-else-if="config.type === 'table-editor' && value")
        .tep-meta
            i.el-icon-fa-table.fas
            span {{ tablePreviewMeta(value) }}
        .tep-table
            .tep-inner(v-html="value")
            .tep-fade
    banner-editor(v-else-if="config.type === 'banner-editor'" :value="value" :edit="false" @open-at="bannerEditorOpenAt = $event")
</template>

<i18n>
{
    "en": {
        "Could not fetch options for the {field} field": "Could not fetch options for the {field} field"
    },
    "de": {
        "Could not fetch options for the {field} field": "Optionen für das {field}-Feld konnten nicht abgerufen werden",
        "Target": "Ziel",
        "Select a targetpage ...": "Zielseite auswählen ...",
        "Find a post ...": "Beitrag suchen ...",
        "Email": "E-Mail",
        "Phone number": "Telefonnummer",
        "Internal": "Intern",
        "External": "Extern",
        "Phone": "Telefon",
        "Download": "Download",
        "Anchor": "Anker",
        "Open link in new window/tab": "Link in einem neuen Fenster/Tab öffnen",
        "Media library": "Mediathek",
        "No file selected": "Keine Datei ausgewählt",
        "Open media library": "Mediathek öffnen",
        "CI colors": "CI-Farben",
        "Standard colors": "Standardfarben",
        "Custom color": "Benutzerdefiniert",
        "Reset": "Zurücksetzen"
    }
}
</i18n>

<script>
import _ from 'lodash';
import axios from 'axios';
import striptags from 'striptags';
import { formatISO } from 'date-fns';
import { useSetupStore, usePageStore } from '@/stores'
import { checkRestrictions } from '@/helpers'

import { language, languageFlag, languageNativeName } from '@/filters'

import quillConfig from '@/quill/config';
import { buildSectionsQuillToolbar } from '@/quill/sectionsOptionalToolbar';
import NestedSet from '@/nested-set-lib';
import store from '@/store';
import PageApi from '@/store/api/cms/page';
import PropertyApi from '@/extensions/shop/store/api/property';

import Treeselect from '@riophae/vue-treeselect';

import Field from './Field';
import PostOptionPreview from './PostOptionPreview'
import ImageField from './ImageField'
import ImageLibraryDialog from './ImageLibraryDialog'

import CodeMirror from '@/components/CodeMirror'
import MonacoEditor from '@/components/MonacoEditor'
import TableEditor from './TableEditor'
import BannerEditor from './BannerEditor'

export default {
    name: 'InputField',
    extends: Field,
    data() {
        // cloned so Section-only toolbar buttons don't leak into the shared @/quill/config singleton
        const localQuillConfig = _.cloneDeep(quillConfig);
        localQuillConfig.bounds = undefined;
        localQuillConfig.modules.link.pages = this.getPageOptions;

        const enabledRteToolbarOptions = useSetupStore().settingValues['rte-toolbar-options'] || [];
        if (enabledRteToolbarOptions.length) {
            localQuillConfig.modules.toolbar.container = buildSectionsQuillToolbar(
                localQuillConfig.modules.toolbar.container,
                enabledRteToolbarOptions
            );

            if (enabledRteToolbarOptions.includes('color')) {
                localQuillConfig.modules.colorPalette = {
                    ciColors: [
                        useSetupStore().settingValues['basic-ci-color-1'],
                        useSetupStore().settingValues['basic-ci-color-2'],
                        useSetupStore().settingValues['basic-highlight-color'],
                    ].filter(Boolean),
                    ciColorsLabel: this.$t('CI colors'),
                    standardColorsLabel: this.$t('Standard colors'),
                    customColorLabel: this.$t('Custom color'),
                    removeLabel: this.$t('Reset'),
                };
            }
        }

        // localQuillConfig.modules.placeholder.variables = [
        //     { label: 'Kundenname', value: 'customer.name' },
        //     { label: 'E-Mail', value: 'customer.email' }
        // ];
        // localQuillConfig.modules.placeholder.variables = this.getTwigVariables;


        return {
            quillConfig: localQuillConfig,
            visibleBlockSettings: false,
            showTranslationOrigin: false,
            mediaLibraryDialogVisible: false,
            quillMediaLibraryDialogVisible: false,
            quillMediaLibraryCallback: null,
            remote: {
                loading: false,
                options: [],
            },
            post: {
                loading: false,
                items: []
            },
            propertyValueSearch: {
                loading: false,
                items: [],
                // number of the newest search; an older answer arriving late is dropped
                latest: 0
            },
            quillPostLinksFeatureEnabled: false,
            bannerEditorOpenAt: -1,
        };
    },
    components: {
        Treeselect,
        PostOptionPreview,
        ImageField,
        ImageLibraryDialog,
        CodeMirror,
        MonacoEditor,
        TableEditor,
        BannerEditor,
    },
    watch: {
        // values of one property mean nothing for another, so a new property starts empty
        'valuesProperty.key'(propertyKey, previousPropertyKey) {
            if (previousPropertyKey && propertyKey !== previousPropertyKey) this.value = []
            this.propertyValueSearch.items = []
        },
        edit() {
            if (this.config.type === 'rte' && this.edit) {
                this.quillConfig.tries = 0;
                this.quillConfig.bounds = undefined;
                this.quillConfig.initialized = false;
                this.$nextTick(this.initializeQuill);
            }
        },
        'quillConfig.bounds'(value) {
            if (value === undefined && this.config.type === 'rte' && this.edit) {
                this.quillConfig.tries = 0;
                this.quillConfig.bounds = undefined;
                this.quillConfig.initialized = false;
                this.$nextTick(this.initializeQuill);
            }
        }
    },
    computed: {
        i18nFeatureEnabled() {
            let features = _.get(this.activeCms, 'config.template.features') || [];
            return !!features.find(el => el === 'i18n');
        },
        i18n() {
            if (this.i18nFeatureEnabled) {
                return _.get(this.activeCms, 'i18n')
            }
        },
        languageOptions() {
            return _.get(this.activeCms, 'languages') || []
        },

        comment() {
            return {
                type: _.get(this.config, 'comment.type') || 'tooltip',
                value: _.get(this.config, 'comment.value') || undefined,
            }
        },

        defaultLanguage() {
            return this.languageOptions.find(language => language.default)
        },
        language() {
            return _.get(this.pageUI.activeTranslation, 'language');
        },
        hasCategories() {
            return this.activeCms.config.template.categories.length >= 1
        },
        linkTypeSetting() {
            const setting = this.settings || [];
            return setting.find(setting => setting.key === 'link_types');
        },
        linkType: {
            get() {
                return _.get(this.content, 'hyperlink.type');
            },
            set(value) {
                this.$emit('change', { key: 'hyperlink.type', value });

                // if (this.c !== undefined && this.config.key) {
                //     const payload = {
                //         value,
                //         i: this.i,
                //         s: this.s,
                //         c: this.c,
                //         field: 'type'
                //     };
                //     store.commit(`${this.module}/form/inputFieldHyperlinkValue`, payload);
                // }

                if (this.content) {
                    this.$set(this.content.hyperlink, 'type', value)
                }
            }
        },
        linkTextSetting() {
            const setting = this.settings || [];
            return setting.find(setting => setting.key === 'link_text');
        },
        linkAnchorSetting() {
            const setting = this.settings || [];
            return setting.find(setting => setting.key === 'link_anchor');
        },
        linkText: {
            get() {
                return _.get(this.content, 'hyperlink.name');
                // || _.get(this.linkTextSetting, 'value')
            },
            set(value) {
                this.$emit('change', { key: 'hyperlink.name', value });

                // if (this.c !== undefined && this.config.key) {
                //     const payload = {
                //         value,
                //         i: this.i,
                //         s: this.s,
                //         c: this.c,
                //         field: 'name'
                //     };
                //     store.commit(`${this.module}/form/inputFieldHyperlinkValue`, payload);
                // }

                if (this.content) {
                    this.$set(this.content.hyperlink, 'name', value)
                }
            }
        },
        linkHref: {
            get() {
                return _.get(this.content, 'hyperlink.href');
            },
            set(value) {
                this.$emit('change', { key: 'hyperlink.href', value });

                // if (this.c !== undefined && this.config.key) {
                //     const payload = {
                //         value,
                //         i: this.i,
                //         s: this.s,
                //         c: this.c,
                //         field: 'href'
                //     };
                //     store.commit(`${this.module}/form/inputFieldHyperlinkValue`, payload);
                // }

                if (this.content) {
                    this.$set(this.content.hyperlink, 'href', value)
                }
            }
        },
        linkAnchor: {
            get() {
                return _.get(this.content, 'hyperlink.anchor');
            },
            set(value) {
                this.$emit('change', { key: 'hyperlink.anchor', value });
                // if (this.c !== undefined && this.config.key) {
                //     const payload = {
                //         value,
                //         i: this.i,
                //         s: this.s,
                //         c: this.c,
                //         field: 'anchor'
                //     };
                //     store.commit(`${this.module}/form/inputFieldHyperlinkValue`, payload);
                // }
                if (this.content) {
                    this.$set(this.content.hyperlink, 'anchor', value)
                }
            }
        },
        linkHrefPlaceholder() {
            let placeholder;

            if (this.linkType === 'internal') {
                placeholder = this.$t('Select a targetpage ...');
            } else if (this.linkTextSetting) {
                switch (this.linkType) {
                    case 'external':
                        placeholder = this.$t('Target');
                        break;

                    case 'mailto':
                        placeholder = this.$t('Email');
                        break;

                    case 'tel':
                        placeholder = this.$t('Phone number');
                        break;
                }
            }

            return placeholder;
        },
        linkTarget: {
            get() {
                return _.get(this.content, 'hyperlink.target') === '_blank';
            },
            set(bool) {
                let value = '_self';
                if (bool === true) value = '_blank';

                this.$emit('change', { key: 'hyperlink.target', value });

                // if (this.c !== undefined && this.config.key) {
                //     const payload = {
                //         value,
                //         i: this.i,
                //         s: this.s,
                //         c: this.c,
                //         field: 'target'
                //     };
                //     store.commit(`${this.module}/form/inputFieldHyperlinkValue`, payload);
                // }

                if (this.content) {
                    this.$set(this.content.hyperlink, 'target', value)
                }
            }
        },
        linkMenuItem: {
            get() {
                return _.get(this.content, 'hyperlink.menuItem.id');
            },
            set(id) {
                let value = null;
                if (id) value = { id };


                this.$emit('change', { key: 'hyperlink.menuItem', value });

                // if (this.c !== undefined && this.config.key) {
                //     const payload = {
                //         value,
                //         i: this.i,
                //         s: this.s,
                //         c: this.c,
                //         field: 'menuItem'
                //     };
                //     store.commit(`${this.module}/form/inputFieldHyperlinkValue`, payload);
                // }

                if (this.content) {
                    this.$set(this.content.hyperlink, 'menuItem', value)
                }
            }
        },
        modalHyperlinkPage: {
            get() {
                return _.get(this.content, 'hyperlink.page.id')
            },
            set(id) {
                let value = null;
                if (id) value = { id };

                this.$emit('change', { key: 'hyperlink.page', value: { id } });

                // if (this.c !== undefined && this.config.key) {
                //     const payload = {
                //         value,
                //         i: this.i,
                //         s: this.s,
                //         c: this.c,
                //         field: 'page'
                //     };
                //     store.commit(`${this.module}/form/inputFieldHyperlinkValue`, payload);
                // }

                if (this.content) {
                    this.$set(this.content.hyperlink, 'page', value)
                }
            }
        },
        treeselectPageOptions() {
            return this.getPageOptions('treeselect');
        },
        clearable() {
            var options = _.get(this.config, 'options') || [];
            return !options.some(function (option) { return option.default; });
        },
        optionValues() {
            return _.map(this.selectOptions, 'value');
        },
        selectOptions() {
            let options = _.get(this.config, 'options') || [];

            options = options.filter(option => {
                return this.checkConditions(option);
            })

            if (this.config.type === 'tags') {
                options = [...options, ...this.tags.map(el => ({ key: el, value: el }))];
            }

            if (['select', 'multi-select', 'tags'].includes(this.config.type) && this.config.remote) {
                options = this.remote.options;
            }

            return options;
        },
        propertyOptions() {
            let properties = _.get(this.options, 'extensions.shop.property.items') || [];
            if (this.config.options && this.config.options.length) {
                if (['select:searchable-properties', 'multi-select:searchable-properties'].includes(this.config.type)) {
                    properties = properties.filter(property => {
                        return !!property.use_in_search && this.config.options.find(option => {
                            return (option.key == null && property.filter_component == null)
                                || (property.filter_component === option.key)
                        })
                    })
                }
            } else {
                if (['select:searchable-properties', 'multi-select:searchable-properties'].includes(this.config.type)) {
                    properties = properties.filter(property => !!property.use_in_search)
                }
            }

            if (['select:sortable-properties', 'multi-select:sortable-properties'].includes(this.config.type)) {
                properties = properties.filter(property => !!property.is_sortable)
            }

            return properties.map(el => ({ key: el.key, value: el.name }))
        },
        properties: {
            get() {
                let properties = [];

                const key = this.config.key;
                if (key) properties = this.content[key] || [];

                return properties.filter(el => this.propertyOptions.find(option => option.key === el.key));
            },
            set(options) {
                // if (!options.length) this.value = null
                this.value = options
            }
        },
        // multi-select:property-values offers the options of the property picked in the
        // setting named by `config.property`, stored as { key, value } pairs (option key, label)
        valuesProperty() {
            const propertyKey = _.get(this.content, `${this.config.property}[0].key`)
            if (!propertyKey) return null
            return (_.get(this.options, 'extensions.shop.property.items') || []).find(property => property.key === propertyKey) || null
        },
        propertyValues: {
            get() {
                return this.content[this.config.key] || []
            },
            set(options) {
                this.value = options
            }
        },
        propertyValueOptions() {
            return _.uniqBy([...this.propertyValues, ...this.propertyValueSearch.items], 'key')
        },
        // select:properties picks one of all shop properties; it is stored as a
        // list like the multi pickers, so the setting lands in `options` on save
        property: {
            get() {
                return this.properties[0] || null
            },
            set(option) {
                this.properties = option ? [option] : []
            }
        },
        subscriptionTopicOptions() {
            let subscriptionTopics = _.get(this.options, 'extensions.newsletter.subscriptionTopic.items') || [];
            let options = subscriptionTopics.map(el => ({ key: el.id, value: el.name }))

            const key = this.config.key;
            if (key) subscriptionTopics = this.content[key] || [];
            subscriptionTopics.forEach(topic => {
                if (!options.find(option => option.key === topic.key)) options.push({ key: topic.key, value: topic.value });
            });

            return options;
        },
        subscriptionTopics: {
            get() {
                let subscriptionTopics = [];

                const key = this.config.key;
                if (key) subscriptionTopics = this.content[key] || [];

                return subscriptionTopics.filter(el => this.subscriptionTopicOptions.find(option => option.key === el.key));
            },
            set(options) {
                // if (!options.length) this.value = null
                this.value = options
            }
        },
        memberTagOptions() {
            let memberTags = _.get(this.options, 'extensions.restrictedArea.memberTag.items') || [];
            let options = memberTags.map(el => ({ key: el.key, value: el.name }))

            const key = this.config.key;
            if (key) memberTags = this.content[key] || [];
            memberTags.forEach(tag => {
                if (!options.find(option => option.key === tag.key)) options.push({ key: tag.key, value: tag.value });
            });

            return options;
        },
        memberTags: {
            get() {
                let memberTags = [];

                const key = this.config.key;
                if (key) memberTags = this.content[key] || [];

                return memberTags.filter(el => this.memberTagOptions.find(option => option.key === el.key));
            },
            set(options) {
                let value = [];
                options.forEach(option => {
                    if (_.isString(option)) value.push({ key: option, value: option });
                    else value.push({ key: option.key, value: option.value });
                });
                this.value = value;
            }
        },
        customerGroupOptions() {
            let customerGroups = _.get(this.options, 'extensions.shop.customerGroup.items') || [];
            return customerGroups.map(el => ({ id: el.id, key: el.key, name: el.name }));
        },
        customerGroup: {
            get() {
                const key = this.config.key;
                if (!key) return null;

                const storedValue = this.content[key];
                if (!storedValue) return null;

                return this.customerGroupOptions.find(option => option.key === storedValue.key) || storedValue;
            },
            set(option) {
                if (!option) {
                    this.value = null;
                } else {
                    this.value = { id: option.id, key: option.key, name: option.name };
                }
            }
        },
        tags: {
            get() {
                let tags = [];

                const key = this.config.key;
                if (key) {
                    if (_.has(this.content, `${key}.value`)) tags = _.map(this.content[key], 'value');
                    else tags = this.content[key];
                }

                if (this.config.type === 'tags') return _.map(tags, 'value');
                return tags;
            },
            set(options) {
                let value = [];
                options.forEach(option => {
                    if (_.isString(option)) value.push({ key: option, value: option });
                    else value.push({ key: option.key, value: option.value });
                });
                this.value = value;
            }
        },
        translationOriginValue() {
            const translationOrigin = _.get(this.content, 'translationOrigin');
            if (translationOrigin) return this.getValue(translationOrigin)
        },
        value: {
            get() {
                return this.getValue(this.content)
            },
            set(value) {
                // if (this.config.field) this.$emit('change', { field: this.config.field, key: this.config.key, value });
                // else this.$emit('change', { key: this.config.key, value });

                // if (this.c !== undefined && this.config.key) {
                //     const payload = {
                //         value,
                //         i: this.i,
                //         s: this.s,
                //         c: this.c,
                //         field: this.config.key
                //     };
                //     store.commit(`${this.module}/form/inputFieldValue`, payload);
                // }

                if (this.config.field) {
                    // content settings
                    this.$emit('change', { field: this.config.field, key: this.config.key, value });

                } else if (this.config.key) {
                    this.$emit('change', { key: this.config.key, value });
                    this.$set(this.content, this.config.key, value)
                }
            }
        },
        numberValue: {
            get() {
                return parseFloat(this.value);
            },
            set(value) {
                this.value = value;
            }
        },
        dateValue: {
            get() {
                const val = this.getValue(this.content);
                return val ? new Date(val) : null;
            },
            set(value) {
                this.value = value ? formatISO(value) : null;
            }
        },
        blockSettingsSupported() {
            let supported = true;
            if (this.config.type === 'linkfield') supported = false;

            return supported;
        },
        inlineSettingsSupported() {
            let supported = true;
            if (this.config.type === 'linkfield') supported = false;

            return supported;
        },
        showLinkTypes() {
            const options = _.get(this.linkTypeSetting, 'options') || [];
            return options.length > 1 || options.length === 0;
        },
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        templateCiColors() {
            const vals = this.setupStore.settingValues || {}
            return [
                vals['basic-ci-color-1'],
                vals['basic-ci-color-2'],
                vals['basic-highlight-color'],
            ].filter(Boolean)
        },
        templateBodyFont() {
            const vals = this.setupStore.settingValues || {}
            const key = vals['basic-font-family']
            const map = {
                'font-roboto-flex':    '"Roboto Flex", sans-serif',
                'font-libre-franklin': '"Libre Franklin", sans-serif',
                'font-rubik':          '"Rubik", sans-serif',
                'font-work-sans':      '"Work Sans", sans-serif',
                'font-shantell-sans':  '"Shantell Sans", sans-serif',
                'font-oswald':         '"Oswald", sans-serif',
                'font-dancing-script': '"Dancing Script", cursive',
                'font-open-sans':      '"Open Sans", sans-serif',
                'font-css-custom':     vals['basic-font-family-custom'] || '',
            }
            return map[key] || ''
        },
        locale() {
            return this.setupStore.locale
        },
        pageStore() {
            return usePageStore()
        },
        pageUI() {
            return this.pageStore.ui
        }

    },
    methods: {
        checkRestrictions,
        getLanguageName(code) {
            return language(code, this.locale ? this.locale.value : 'de')
        },
        getLanguageFlag(code) {
            return languageFlag(code)
        },
        getLanguageNativeName(code) {
            return languageNativeName(code)
        },

        getValue(content) {
            let defaultValue = _.get(this.config, 'value');

            if (this.config.type === 'select') {
                const defaultOption = this.selectOptions.find(el => ['true', '1', 'yes', true].includes(el.default));
                if (defaultOption) defaultValue = defaultOption.key;
            }

            let result;
            switch (this.config.type) {
                case 'switch':
                case 'checkbox':
                    const value = _.get(content, this.config.key, defaultValue || false);
                    result = ['true', '1', 'yes', true].includes(value);
                    break;

                default:
                    result = _.get(content, this.config.key, defaultValue || '');
            }

            return result;
        },
        isLinkTypeActive(key) {
            const options = _.get(this.linkTypeSetting, 'options') || [];
            return options.length === 0 || _.find(options, { key });
        },
        getTwigVariables() {
            let variables = _.get(this.config, 'twigVariables') || [];
            return variables.filter(variable => this.checkConditions(variable))
                .map(el => ({ label: el.value, value: el.key }))
        },
        getPageOptions(target = 'quill') {
            let menus = _.get(this.activeCms, 'config.template.menus', []);
            menus = _.cloneDeep(menus).sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0));

            const menuOptions = _.get(this.options, 'menu.items') || [];
            const visibleOptgroups = menuOptions.filter(el => el.menuItems.length).length > 1;

            let tree = [];
            menus.forEach(menu => {
                // reduce tree for quill
                const menuOption = _.find(menuOptions, { key: menu.key });
                let items = _.get(menuOption, 'menuItems', []);
                if (items.length) {
                    items = _.cloneDeep(items);
                    if (this.i18n && this.language) {
                        items.forEach(item => {
                            if (item.translations) {
                                const translation = item.translations.find(el => el.language.code === this.language.code);
                                if (translation) {
                                    item.subTitle = translation.title;
                                    if (target === 'quill') item.path = translation.path;
                                }
                            }
                        })
                    }
                }

                if (target === 'quill') {
                    let dir = { folder: menu.name, children: [] };
                    let children = [];

                    // current menu
                    // get items from current form state
                    // if (menu.key === this.form.key) {
                    //     let items = _.get(this.form, 'menuItems', []);

                    //     if (items.length) {
                    //         items = _.cloneDeep(items);
                    //         items.forEach(item => {
                    //             item.disabled = false;
                    //             if (item.deleted === true) item.disabled = true;
                    //             // check if same as current item
                    //             if (this.currentItem !== null && this.currentItem.id === item.id) item.disabled = true;
                    //         });
                    //         children = NestedSet.createTree(items);
                    //     }
                    // } else {
                        // const menuOption = _.find(menuOptions, { key: menu.key });
                        // let items = _.get(menuOption, 'menuItems', []);

                        if (items.length) {
                            // items = _.cloneDeep(items);
                            children = NestedSet.createTree(items);
                        }
                    // }

                    dir.children = children;
                    if (children.length) tree.push(dir);
                } else {
                    // current menu
                    // get items from current form state
                    // if (menu.key === this.form.key) {
                    //     let items = _.get(this.form, 'menuItems', []);

                    //     if (items.length) {
                    //         const optgroup = {
                    //             id: `optgroup-${this.form.id}`,
                    //             title: this.form.name,
                    //             isDisabled: true,
                    //             optgroup: true
                    //         };

                    //         items = _.cloneDeep(items);
                    //         items.forEach(item => {
                    //             if (item.deleted === true) item.isDisabled = true;
                    //             // check if same as current item
                    //             if (this.currentItem !== null && this.currentItem.id === item.id)
                    //                 item.isDisabled = true;
                    //         });
                    //         if (visibleOptgroups) tree.push(optgroup);
                    //         tree = [...tree, ...NestedSet.createTree(items)];
                    //     }
                    // } else {
                        // const menuOption = _.find(menuOptions, { key: menu.key });
                        // let items = _.get(menuOption, 'menuItems', []);

                        if (items.length) {
                            // items = _.cloneDeep(items);

                            const optgroup = {
                                id: `optgroup-${menuOption.id}`,
                                title: menuOption.name,
                                isDisabled: true,
                                optgroup: true
                            };

                            if (visibleOptgroups) tree.push(optgroup);
                            tree = [...tree, ...NestedSet.createTree(items)];
                        }
                    // }
                }
            });

            return tree;
        },
        async getPagesPaginated(searchString = '') {
            const categories = _.get(this.activeCms, 'config.template.categories') || [];
            let data = {
                filters: [{
                    "key": "title",
                    "like": searchString
                },
                {
                    "key": "cms",
                    "match": this.activeCms.id
                },
                {
                    "in": categories.map(el => el.key),
                    "key": "category_keys"
                }],
                variables: {
                    order: 'created_at',
                    direction: 'desc',
                    withCategory: true
                },
            };

            let pageData;
            await PageApi.fetchPagesPaginated(data).then(data => {
                pageData = data.items;
            });
            return pageData;
        },
        tablePreviewMeta(html) {
            if (!html) return ''
            const doc = new DOMParser().parseFromString(html, 'text/html')
            const table = doc.querySelector('table')
            if (!table) return ''
            const rows = table.querySelectorAll('tr').length
            const firstRow = table.querySelector('tr')
            const cols = firstRow ? firstRow.querySelectorAll('th, td').length : 0
            const hasHeader = !!table.querySelector('thead')
            return `${rows} Zeile${rows !== 1 ? 'n' : ''} × ${cols} Spalte${cols !== 1 ? 'n' : ''}${hasHeader ? ' · Kopfzeile' : ''}`
        },
        striptags(html) {
            if (!html) return '';
            html = html.replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '<br />');
            return striptags(html, ['a', 'i', 'u', 'b', 'em', 'strong', 'span', 'br']);
        },
        selectFileFromLibrary(file) {
            this.linkHref = file.url;
            this.mediaLibraryDialogVisible = false;
        },
        openMediaLibraryForQuill(callback) {
            this.quillMediaLibraryCallback = callback;
            this.quillMediaLibraryDialogVisible = true;
        },
        selectFileFromQuillLibrary(file) {
            if (typeof this.quillMediaLibraryCallback === 'function') {
                this.quillMediaLibraryCallback(file.url);
            }
            this.quillMediaLibraryCallback = null;
            this.quillMediaLibraryDialogVisible = false;
        },
        findPropertyValues(query = '') {
            if (!this.valuesProperty) return

            const search = ++this.propertyValueSearch.latest
            this.propertyValueSearch.loading = true
            PropertyApi.fetchValueOptionsPaginated({
                property: parseInt(this.valuesProperty.id),
                currentPage: 1,
                perPage: 50,
                filters: query ? [{ key: 'search', like: query }] : [],
            })
                .then(pagination => {
                    if (search !== this.propertyValueSearch.latest) return
                    this.propertyValueSearch.items = (pagination.items || []).map(option => ({ key: option.key, value: option.value || option.text || option.key }))
                })
                .finally(() => {
                    if (search === this.propertyValueSearch.latest) this.propertyValueSearch.loading = false
                })
        },
        findPost(query = '') {
            let items = []
            if (this.content.hyperlink) {
                if (this.content.hyperlink.page) items.push(this.content.hyperlink.page)
            }

            this.post.loading = true
            this.pageStore.findCategoryPosts(query)
                .then(data => {
                    items = [...items, ...data.items]
                    this.post.items = _.orderBy(_.uniqBy(items, 'id'), 'created_at', 'desc')
                })
                .finally(() => this.post.loading = false)
        },
        // The page being edited is the AI's context. Inside a newsletter the store
        // holds the newsletter's page, which brings subject and preheader along;
        // global contents and unsaved pages have no page to name.
        textGenerationContext() {
            const pageId = String(_.get(this.pageStore, 'form.id') || '');
            if (this.pageStore.isGlobalContentsMode || !pageId || pageId.startsWith('new')) return {};

            return { type: 'page', id: pageId };
        },
        initializeQuill() {
            if (this.quillConfig.initialized) return;

            //>>> add quill config depending on template features
            const features = _.get(this.activeCms, 'config.template.features') || []
            this.quillPostLinksFeatureEnabled = !!features.find(el => el === 'quill:post-links');
            if (this.quillPostLinksFeatureEnabled) {
                if (!this.quillConfig.modules.link.types.includes('posts')) {
                    this.quillConfig.modules.link.types.splice(1, 0, 'posts');
                }
                this.quillConfig.modules.link.posts = this.getPagesPaginated;
            }
            this.quillConfig.modules.link.mediaLibrary = this.openMediaLibraryForQuill;
            //<<<

            this.quillConfig.modules.placeholder.variables = this.getTwigVariables();
            this.quillConfig.modules.textGeneration.context = this.textGenerationContext;

            if (this.$refs.container && this.$refs.container.offsetWidth) {
                this.quillConfig.initialized = true;
                this.quillConfig.bounds = this.$refs.container;
            } else {
                this.quillConfig.tries++;
                setTimeout(() => {
                    if (this.quillConfig.tries < 100) this.initializeQuill();
                    else this.quillConfig.bounds = '#app';
                }, 10)
            }
        },
    },
    mounted() {
        if (['select', 'multi-select', 'tags'].includes(this.config.type) && this.config.remote) {
            const method = _.get(this.config, 'request_type').toLowerCase() || 'get';

            let headers = {}
            const token = _.get(this.config, 'token') || null;
            if (token) {
                headers = {
                    'Authorization': `Bearer ${token}`
                }
            }

            this.remote.loading = true;
            this.remote.options = this.tags;

            // fetch options from remote using method
            axios({ method, url: this.config.remote, headers })
                .then(({ data }) => {
                    if (!data || !data.length) {
                        throw new Error('Missing data');
                    }

                    this.remote.options = data;
                })
                .catch(error => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Could not fetch options for the {field} field', { field: this.config.name }),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .finally(() => {
                    this.remote.loading = false;
                });
        }

        if (this.config.type === 'rte' && this.edit) {
            this.quillConfig.tries = 0;
            this.quillConfig.initialized = false;
            this.$nextTick(this.initializeQuill);
        }

        if (this.config.type === 'linkfield') {

            //find first posts
            this.findPost()

            if (!this.linkType) {
                let type = 'internal';

                const options = _.get(this.linkTypeSetting, 'options') || [];
                if (options.length) {
                    const option = _.find(options, 'default');
                    if (option) type = option.key;
                }

                this.linkType = type;
            }

            if (!this.linkText) {
                this.linkText = _.get(this.linkTextSetting, 'value');
            }
        }
        this.$nextTick(() => {
            const placeholder = this.config.placeholder;
            if (this.config.type === 'rte' && this.$refs.quill && placeholder) {
                this.$refs.quill.$el.querySelector('.ql-editor').setAttribute('data-placeholder', placeholder);
            }
        });
    }
};
</script>

<style lang="sass" scoped>
.table-editor-preview
    border: 1px solid #EBEEF5
    border-radius: 4px
    overflow: hidden
    background: #fff

.tep-meta
    display: flex
    align-items: center
    gap: 5px
    padding: 4px 8px
    background: #f5f7fa
    border-bottom: 1px solid #EBEEF5
    font-size: 11px
    color: #909399
    i
        font-size: 10px

.tep-table
    position: relative
    max-height: 96px
    overflow: hidden

.tep-fade
    position: absolute
    bottom: 0
    left: 0
    right: 0
    height: 28px
    background: linear-gradient(transparent, #fff)
    pointer-events: none

// table rendering in preview
.tep-inner
    ::v-deep table
        border-collapse: collapse
        width: 100% !important
        font-size: 11px
        line-height: 1.4
        margin: 0 !important

    ::v-deep th
        padding: 4px 8px
        background: #f5f7fa
        color: #909399
        font-weight: 600
        text-align: left
        white-space: nowrap
        font-size: 10px
        text-transform: uppercase
        letter-spacing: 0.03em

    ::v-deep td
        padding: 3px 8px
        color: #606266
        text-align: left
        white-space: nowrap

    // border variants
    ::v-deep table.tbl-border-full th,
    ::v-deep table.tbl-border-full td
        border: 1px solid var(--tv2-border-color, #EBEEF5)

    ::v-deep table.tbl-border-rows tbody tr
        border-bottom: 1px solid var(--tv2-border-color, #EBEEF5)

    ::v-deep table.tbl-border-rows thead tr
        border-bottom: 2px solid var(--tv2-border-color, #EBEEF5)

    ::v-deep table.tbl-border-none th,
    ::v-deep table.tbl-border-none td
        border: none

    // zebra
    ::v-deep table.tbl-zebra tbody tr:nth-child(even) td
        background-color: var(--tv2-zebra-color, #f3f4f6)

    // cell bg
    ::v-deep td
        background-color: var(--tv2-bg-color, #fff)

.rte
    word-break: normal !important

.input-field.inline-comment
    .flex-fields
        display: block !important
    .comment
        display: flex
        justify-content: flex-end

.comment
    .tooltip
        margin-left: 0.5rem
        .el-button
            padding: 0
            height: 28px
            width: 28px
            line-height: 20px
            text-align: center
            font-weight: 500
            background: #f9fafc
            &:focus, &:active
                border-color: #dde0e6
                color: #606266
    .inline
        margin-top: 0.65rem
        width: 100%
        max-width: 695px
        text-align: left


.field .comment
    .tooltip
        padding-top: 26px

.input-field > hr
    margin: 0 !important

.input-field.is-banner-editor
    ::v-deep .el-form-item__label
        display: none
    ::v-deep .el-form-item__content
        margin-left: 0 !important

.input-field.divider
    display: flex
    gap: 0.5rem
    justify-content: center
    align-items: center
    color: $color-grey-text
    // margin-top: 3rem
    margin-top: 2rem
    margin-bottom: 2rem
    font-size: 1rem
    font-weight: 300
    hr
        flex-grow: 1
        border: none
        border-top: 1px solid $--border-color-base

.dummy.ql-editor[disabled]
    opacity: 0.5

.has-block-settings
    .translation-origin
        margin-right: -40px
        z-index: 1
        & + .el-input
            z-index: 2

.vue-treeselect__label-container label
    small
        position: relative
        display: block
        line-height: 1
        color: rgba(0, 0, 0, 0.35)
        top: -5px

.vue-treeselect__single-value
    small
        font-size: 1em
        color: rgba(0, 0, 0, 0.35)
        &:before
            content: ' / '

</style>
